import { useMemo } from 'react';
import classNames from 'classnames';

import { DatePicker } from '../../../formControls/DatePicker/DatePicker';
import { Select } from '../../../formControls/Select/Select';
import { renderReccuringTypes } from '../../partials/renderReccuringTypes';
import {
  DEFAULT_TIME_FORMAT,
  RECURRING_REPEATS_OPTIONS,
  TIMEZONES,
  URLS,
} from '../../../../constants/constants';
import { endsAfterOptions } from '../../mock';
import { useRecurringInfo } from './useRecurringInfo';
import { DateHelper } from '../../../../utils';
import { RecurringRidePlanSummary } from '../../../common/RecurringRidePlanSummary/RecurringRidePlanSummary';

import styles from './index.module.css';
import { datePickerFieldsNames } from '../../../../utils/helpers';
import { useLocation } from 'react-router-dom';
import {
  DATE_TIME_FIELD_LABELS,
  DATE_TIME_LABEL_VARIANT,
  getQuickScheduleDatePickerPopupContainer,
  getRecurringStartPointTitle,
  QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS,
} from './stepThreeDateTimeConstants';

export const RecurringSection = ({
  values,
  validateForm,
  pickUpDate,
  startPointOfRecurring,
  disabledReccuringValues,
  setStartPointOfRecurring,
  setReturnStartPoint,
  handleChangeTZ,
  returnStartPoint,
  handleStartPointTime,
  handleReturnStartPoint,
  disabledDatesForStartPointOfRecurring,
  disabledDatesForReturnTimeForRoundTripRecurring,
  disabledDateTime,
  minDelayToStartRide,
  minimumRoundTrip,
  labelVariant = DATE_TIME_LABEL_VARIANT.DEFAULT,
  fieldInputClassName,
  selectFieldClassName,
}) => {
  const isScheduleRide = labelVariant === DATE_TIME_LABEL_VARIANT.SCHEDULE_RIDE;
  const datePickerClass = fieldInputClassName || undefined;
  const selectClass = selectFieldClassName || fieldInputClassName || undefined;
  const location = useLocation();
  const { monthPickerOptions, handleChangeEndsAfter, handleRecurringFrequency, recurringType } = useRecurringInfo({
    values,
    validateForm,
    pickUpDate,
    startPointOfRecurring,
    disabledReccuringValues,
    setStartPointOfRecurring,
    setReturnStartPoint,
  });
  const recurringDateFieldsAreDisabled = useMemo(() => {
    return (
      isScheduleRide ||
      location.pathname === URLS.CREATE_RIDE ||
      location.pathname === URLS.EDIT_DRAFT
    );
  }, [isScheduleRide, location]);

  const startPointFieldTitle = useMemo(
    () => getRecurringStartPointTitle(values?.isRoundtrip),
    [values?.isRoundtrip],
  );

  const minAvailableStartDate = useMemo(
    () =>
      DateHelper.addDates(
        DateHelper.utcToZonedTime(Date.now(), values?.recurrenceRule?.startPoint?.timezone),
        minDelayToStartRide ?? { hours: 0, minutes: 0 },
      ),
    [values?.recurrenceRule?.startPoint?.timezone, minDelayToStartRide],
  );

  const disabledDatesForStartPoint = useMemo(
    () => disabledDatesForStartPointOfRecurring(minAvailableStartDate),
    [disabledDatesForStartPointOfRecurring, minAvailableStartDate],
  );

  const disabledTimeForStartPoint = useMemo(
    () => disabledDateTime(minAvailableStartDate, startPointOfRecurring),
    [disabledDateTime, minAvailableStartDate, startPointOfRecurring],
  );

  const rule = values?.recurrenceRule;

  return (
    <div
      className={classNames({
        qsScheduleRecurringRoot: isScheduleRide,
      })}
    >
      {!recurringDateFieldsAreDisabled && (
        <>
          <div
            className={classNames(styles.statPointOfRecurring, {
              qsScheduleRecurringStartRow: isScheduleRide,
            })}
          >
            <div
              className={classNames(styles.dateAndTime, {
                qsScheduleRecurringGrid: isScheduleRide,
              })}
            >
              <label
                className={classNames(styles.timezone, {
                  qsScheduleRecurringField: isScheduleRide,
                })}
              >
                <span className={styles.required}>{DATE_TIME_FIELD_LABELS.TIMEZONE}</span>
                <Select
                  name={datePickerFieldsNames.startPoint.timezone}
                  onChange={handleChangeTZ}
                  options={TIMEZONES}
                  placeholder="Pick one"
                  myClassName={selectClass}
                  disabled={disabledReccuringValues || recurringDateFieldsAreDisabled}
                  whenFieldDisabledShowTooltip={Boolean(disabledReccuringValues)}
                />
              </label>
              <label
                className={classNames(styles.datepicker, {
                  qsScheduleRecurringField: isScheduleRide,
                })}
              >
                <span className={styles.required}>{startPointFieldTitle}</span>
                <DatePicker
                  name={datePickerFieldsNames.startPoint.datetime}
                  myClassName={datePickerClass}
                  onChange={handleStartPointTime}
                  format={DEFAULT_TIME_FORMAT}
                  value={startPointOfRecurring}
                  showTime
                  showNow={false}
                  use12Hours
                  disabledDate={disabledDatesForStartPoint}
                  disabledTime={disabledTimeForStartPoint}
                  disabled={
                    !values?.recurrenceRule?.startPoint?.timezone ||
                    disabledReccuringValues ||
                    recurringDateFieldsAreDisabled
                  }
                  whenFieldDisabledShowTooltip={Boolean(disabledReccuringValues)}
                  inputReadOnly
                  popupRootClassName={
                    isScheduleRide ? QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS : undefined
                  }
                  getPopupContainer={
                    isScheduleRide ? getQuickScheduleDatePickerPopupContainer : undefined
                  }
                />
              </label>
            </div>
          </div>
          {values?.isRecurring && values?.isRoundtrip && (
            <div>
              <div
                className={classNames(styles.dateAndTime, {
                  qsScheduleRecurringGrid: isScheduleRide,
                })}
              >
                <label
                  className={classNames(styles.timezone, {
                    qsScheduleRecurringField: isScheduleRide,
                  })}
                >
                  <span className={styles.required}>{DATE_TIME_FIELD_LABELS.TIMEZONE}</span>
                  <Select
                    name={datePickerFieldsNames.returnStartPoint.timezone}
                    options={TIMEZONES}
                    onChange={handleChangeTZ}
                    placeholder="Pick one"
                    myClassName={selectClass}
                    disabled={disabledReccuringValues || recurringDateFieldsAreDisabled}
                    whenFieldDisabledShowTooltip={Boolean(disabledReccuringValues)}
                  />
                </label>
                <div
                  className={classNames(styles.datepicker, styles.nestedDatepicker, {
                    qsScheduleRecurringField: isScheduleRide,
                  })}
                >
                  <span className={styles.required}>
                    {DATE_TIME_FIELD_LABELS.RECURRING_START_LEG_2}
                  </span>
                  <DatePicker
                    name={datePickerFieldsNames.returnStartPoint.datetime}
                    myClassName={datePickerClass}
                    onChange={handleReturnStartPoint}
                    format={DEFAULT_TIME_FORMAT}
                    value={returnStartPoint}
                    showTime
                    showNow={false}
                    use12Hours
                    disabledDate={disabledDatesForReturnTimeForRoundTripRecurring}
                    disabledTime={disabledDateTime(
                      DateHelper.addDates(startPointOfRecurring, minimumRoundTrip),
                      returnStartPoint,
                    )}
                    disabled={
                      !values?.recurrenceRule?.returnStartPoint?.timezone ||
                      !values?.recurrenceRule?.startPoint?.datetime ||
                      disabledReccuringValues ||
                      recurringDateFieldsAreDisabled
                    }
                    whenFieldDisabledShowTooltip={Boolean(disabledReccuringValues)}
                    inputReadOnly
                    popupRootClassName={
                      isScheduleRide ? QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS : undefined
                    }
                    getPopupContainer={
                      isScheduleRide ? getQuickScheduleDatePickerPopupContainer : undefined
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {values?.recurrenceRule && (
        <div
          className={classNames(styles.recurringInfo, {
            qsScheduleRecurringGrid: isScheduleRide,
          })}
        >
          <label
            className={classNames(styles.repeats, {
              qsScheduleRecurringField: isScheduleRide,
            })}
          >
            <span className={styles.required}>Repeats</span>
            <Select
              name="recurrenceRule.frequency"
              options={RECURRING_REPEATS_OPTIONS}
              placeholder="Pick one"
              value={recurringType}
              myClassName={selectClass}
              disabled={!values?.pickUpDate?.datetime || disabledReccuringValues}
              onChange={handleRecurringFrequency}
              whenFieldDisabledShowTooltip={Boolean(disabledReccuringValues)}
            />
          </label>

          <label
            className={classNames(styles.repeats, {
              qsScheduleRecurringField: isScheduleRide,
            })}
          >
            <span className={styles.required}>Ends After</span>
            <Select
              name="recurrenceRule.count"
              options={endsAfterOptions}
              placeholder="Number of rides"
              myClassName={selectClass}
              onChange={handleChangeEndsAfter}
              value={
                values?.recurrenceRule?.count &&
                `${values.recurrenceRule.count} ${values?.isRoundtrip ? 'roundtrip rides' : 'rides'}`
              }
              disabled={!values?.pickUpDate?.datetime || disabledReccuringValues}
              whenFieldDisabledShowTooltip={Boolean(disabledReccuringValues)}
            />
          </label>
        </div>
      )}
      {renderReccuringTypes({
        recurringType,
        values,
        monthPickerOptions,
        showDisabledTooltip: Boolean(disabledReccuringValues),
        disabled: disabledReccuringValues,
      })}
      <RecurringRidePlanSummary
        isRecurring={values?.isRecurring}
        isRoundtrip={values?.isRoundtrip}
        recurrenceRule={rule}
        pickUpDate={values?.pickUpDate}
        variant="form"
        dense={isScheduleRide}
      />
    </div>
  );
};
